import React, { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Building2, Search, Loader2, RefreshCw, ChevronLeft, ChevronRight, Settings,
} from 'lucide-react';
import { toast } from 'sonner';
import adminService from '../../services/adminService';

const SUB_STATUSES = ['Paid', 'Pending', 'Expired'];

export default function AdminOrganizationsPage() {
  const [data, setData] = useState({ items: [], total: 0, page: 1, pageSize: 25, totalPages: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const [selectedOrg, setSelectedOrg] = useState(null);
  const [isSubDialogOpen, setIsSubDialogOpen] = useState(false);
  const [newStatus, setNewStatus] = useState('Paid');
  const [isSaving, setIsSaving] = useState(false);

  const fetchOrgs = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await adminService.getOrganizations({ search: search || undefined, page, pageSize: 25 });
      setData(result);
    } catch (error) {
      toast.error(typeof error === 'string' ? error : 'Failed to load organizations');
    } finally {
      setIsLoading(false);
    }
  }, [search, page]);

  useEffect(() => { fetchOrgs(); }, [fetchOrgs]);

  const openSubDialog = (org) => {
    setSelectedOrg(org);
    setNewStatus(org.subscriptionStatus === 'Active' ? 'Paid' : 'Pending');
    setIsSubDialogOpen(true);
  };

  const handleOverrideStatus = async () => {
    if (!selectedOrg) return;
    setIsSaving(true);
    try {
      await adminService.overrideSubscriptionStatus(selectedOrg.organizationId, newStatus);
      toast.success('Subscription status updated');
      setIsSubDialogOpen(false);
      fetchOrgs();
    } catch (error) {
      toast.error(typeof error === 'string' ? error : 'Failed to update status');
    } finally {
      setIsSaving(false);
    }
  };

  const subBadge = (status) => {
    if (status === 'Active' || status === 'Paid')
      return <Badge className="bg-green-100 text-green-800">Active</Badge>;
    if (status === 'Expired')
      return <Badge className="bg-red-100 text-red-800">Expired</Badge>;
    return <Badge className="bg-yellow-100 text-yellow-800">{status ?? 'None'}</Badge>;
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Organizations</h1>
            <p className="text-muted-foreground mt-1">Manage tenant organizations and subscriptions</p>
          </div>
          <Button variant="outline" size="sm" onClick={fetchOrgs}>
            <RefreshCw className="h-4 w-4 mr-2" />Refresh
          </Button>
        </div>

        {/* Search */}
        <Card>
          <CardContent className="p-4">
            <form onSubmit={e => { e.preventDefault(); setPage(1); fetchOrgs(); }} className="flex gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name or email..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Button type="submit">Search</Button>
            </form>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardHeader>
            <CardTitle>Organizations</CardTitle>
            <CardDescription>{data.total} total organizations</CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex items-center justify-center h-40">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : (
              <>
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Users</TableHead>
                        <TableHead>Plan</TableHead>
                        <TableHead>Subscription</TableHead>
                        <TableHead>Expires</TableHead>
                        <TableHead>Created</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.items?.map(org => (
                        <TableRow key={org.organizationId}>
                          <TableCell className="font-medium">{org.name}</TableCell>
                          <TableCell className="text-muted-foreground">{org.email || '—'}</TableCell>
                          <TableCell>{org.userCount}</TableCell>
                          <TableCell>{org.activePlanName || '—'}</TableCell>
                          <TableCell>{subBadge(org.subscriptionStatus)}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {org.subscriptionEndDate ? new Date(org.subscriptionEndDate).toLocaleDateString() : '—'}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {new Date(org.createdAt).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            <Button variant="ghost" size="sm" onClick={() => openSubDialog(org)} title="Override Subscription">
                              <Settings className="h-4 w-4 text-blue-500" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                <div className="flex items-center justify-between mt-4">
                  <p className="text-sm text-muted-foreground">
                    Page {data.page} of {data.totalPages} ({data.total} records)
                  </p>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" size="sm" disabled={page >= data.totalPages} onClick={() => setPage(p => p + 1)}>
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Override Subscription Status Dialog */}
      <Dialog open={isSubDialogOpen} onOpenChange={setIsSubDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Override Subscription</DialogTitle>
            <DialogDescription>{selectedOrg?.name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1">
              <Label>Payment Status</Label>
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SUB_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Button className="flex-1" onClick={handleOverrideStatus} disabled={isSaving}>
                {isSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                Apply
              </Button>
              <Button variant="outline" className="flex-1" onClick={() => setIsSubDialogOpen(false)}>Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
